import { useState, useCallback, useEffect } from 'react';
import styles from './index.module.scss';
import SiteTopNav from '@/components/SiteTopNav';
import galleryHero from '@/assets/images/pages-gallery.png';

const CDN_BASE = 'https://cdn.sakurafloors.com/assets/gallery/';

const galleryImages = [
  { url: `${CDN_BASE}Honey%20Amber%20Prime01.png`, filename: 'Honey Amber Prime01.png' },
  { url: `${CDN_BASE}Misty%20White%20Oak02.png`, filename: 'Misty White Oak02.png' },
  { url: `${CDN_BASE}Morning%20Fog01.png`, filename: 'Morning Fog01.png' },
  { url: `${CDN_BASE}Toasted%20Almond01.png`, filename: 'Toasted Almond01.png' },
  { url: `${CDN_BASE}Aurora%20Glow1.png`, filename: 'Aurora Glow1.png' },
  { url: `${CDN_BASE}Aurora%20Glow2.png`, filename: 'Aurora Glow2.png' },
  { url: `${CDN_BASE}Aurora%20Glow3.png`, filename: 'Aurora Glow3.png' },
  { url: `${CDN_BASE}Buttered%20Wheat1.png`, filename: 'Buttered Wheat1.png' },
  { url: `${CDN_BASE}Golden%20Horizon1.png`, filename: 'Golden Horizon1.png' },
  { url: `${CDN_BASE}Golden%20Horizon2.png`, filename: 'Golden Horizon2.png' },
  { url: `${CDN_BASE}Golden%20Horizon3.png`, filename: 'Golden Horizon3.png' },
  { url: `${CDN_BASE}Harvest%20Wheat1.png`, filename: 'Harvest Wheat1.png' },
  {
    type: 'video',
    url: `${CDN_BASE}Harvest%20Wheat%20Video01.mp4`,
    poster: `${CDN_BASE}Harvest%20Wheat%20Video01thumbnail.jpeg`,
    filename: 'Harvest Wheat Video01.mp4',
    title: 'Harvest Wheat',
  },
  { url: `${CDN_BASE}Honey%20Amber01.png`, filename: 'Honey Amber01.png' },
  { url: `${CDN_BASE}Honey%20Amber02.png`, filename: 'Honey Amber02.png' },
  { url: `${CDN_BASE}Honey%20Amber03.png`, filename: 'Honey Amber03.png' },
  { url: `${CDN_BASE}Misty%20White%20Oak01.png`, filename: 'Misty White Oak01.png' },
];

const getDisplayName = filename => {
  return filename
    .replace(/\.(png|jpg|jpeg|webp|mp4|mov|webm)$/i, '')
    .replace(/\d+$/, '')
    .trim();
};

const PlayOverlay = () => (
  <span className={styles['play-overlay']} aria-hidden="true">
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="24" r="22" fill="rgba(255,255,255,0.88)" />
      <path d="M20 16L34 24L20 32V16Z" fill="rgba(22,0,4,0.75)" />
    </svg>
  </span>
);

const Gallery = () => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);

  const images = galleryImages;

  const selectedImage = selectedImageIndex !== null ? images[selectedImageIndex] : null;

  const handlePrevious = useCallback(() => {
    setSelectedImageIndex(prev => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const handleNext = useCallback(() => {
    setSelectedImageIndex(prev => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  const handleClose = useCallback(() => {
    setSelectedImageIndex(null);
  }, []);

  useEffect(() => {
    if (selectedImageIndex === null) return;

    const handleKeyDown = e => {
      // Native <video controls> uses Left/Right for seeking when focused — don't
      // also advance slides in that case, but Escape should always close.
      const isVideoFocused = document.activeElement?.tagName === 'VIDEO';
      if (e.key === 'ArrowLeft' && !isVideoFocused) handlePrevious();
      else if (e.key === 'ArrowRight' && !isVideoFocused) handleNext();
      else if (e.key === 'Escape') handleClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImageIndex, handlePrevious, handleNext, handleClose]);

  return (
    <div className={styles['gallery-page']}>
      <div className={styles.page}>
        {/* Hero Section */}
        <section
          className={styles['gallery-hero']}
          style={{ backgroundImage: `url(${galleryHero})` }}
        >
          <div className={styles['gallery-hero__overlay']}>
            <SiteTopNav />
            <div className={styles['gallery-hero__content']}>
              <h1 className={styles['gallery-hero__title']} data-motion="hero">
                Gallery
              </h1>
              <p className={styles['gallery-hero__subtitle']} data-motion="copy">
                Explore the beauty and elegance of our finest flooring installations
              </p>
            </div>
          </div>
        </section>

        {/* Gallery Grid */}
        <main className={styles['gallery-main']}>
          <div className={styles['gallery-grid']}>
            {images.map((image, index) => (
              <article
                key={`${image.filename}-${index}`}
                className={styles['gallery-item']}
                onClick={() => setSelectedImageIndex(index)}
                role="button"
                tabIndex={0}
                aria-label={
                  image.type === 'video' ? `Play video ${index + 1}` : `View image ${index + 1}`
                }
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setSelectedImageIndex(index);
                  }
                }}
              >
                <img
                  src={image.type === 'video' ? image.poster : image.url}
                  alt={image.filename}
                  className={styles['gallery-image']}
                />
                {image.type === 'video' ? <PlayOverlay /> : null}
                <div className={styles['gallery-overlay']} aria-hidden="true">
                  <div className={styles['gallery-overlay__content']}>
                    <h3 className={styles['gallery-overlay__title']}>
                      {image.title || getDisplayName(image.filename)}
                    </h3>
                    <div className={styles['gallery-overlay__line']} />
                    <span className={styles['gallery-overlay__cta']}>EXPLORE</span>
                    <div className={styles['gallery-overlay__line']} />
                  </div>
                </div>
              </article>
            ))}
          </div>
        </main>
      </div>

      {/* Lightbox */}
      {selectedImage && (
        <div
          className={styles['lightbox']}
          onClick={e => {
            if (e.target === e.currentTarget) handleClose();
          }}
          role="dialog"
          aria-label={selectedImage.type === 'video' ? 'Video lightbox' : 'Image lightbox'}
          aria-modal="true"
        >
          <button
            className={styles['lightbox__close']}
            onClick={handleClose}
            aria-label="Close lightbox"
            type="button"
          >
            ✕
          </button>

          <button
            className={`${styles['lightbox__nav']} ${styles['lightbox__nav--prev']}`}
            onClick={handlePrevious}
            aria-label="Previous image"
            type="button"
          >
            ‹
          </button>

          <div className={styles['lightbox__content']}>
            {selectedImage.type === 'video' ? (
              // No `muted`: opening the lightbox is itself a user gesture, so we
              // attempt unmuted autoplay. Some browsers' autoplay heuristics may
              // still block it — the fallback is just a paused video with the
              // native controls visible, no error state to handle.
              <video
                key={selectedImage.url}
                className={styles['lightbox__video']}
                src={selectedImage.url}
                poster={selectedImage.poster}
                controls
                autoPlay
                playsInline
                preload="metadata"
              />
            ) : (
              <img
                src={selectedImage.url}
                alt={selectedImage.filename}
                className={styles['lightbox__image']}
              />
            )}
          </div>

          <button
            className={`${styles['lightbox__nav']} ${styles['lightbox__nav--next']}`}
            onClick={handleNext}
            aria-label="Next image"
            type="button"
          >
            ›
          </button>

          <div className={styles['lightbox__counter']} aria-live="polite">
            {selectedImageIndex + 1} / {images.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
